import { useEffect, useMemo, useRef, useState } from 'react';

import { isMotionReduced } from '#src/app/shared/utils/accessibility';

// 50% visibility, not 100%: the video's height is driven by the taller right-hand column
// (`md:h-full`), and on common desktop sizes it's taller than the viewport, so full visibility
// can never be reached there — that would make autoplay unreachable in practice.
const AUTOPLAY_VISIBILITY_THRESHOLD = 0.5;

type Params = {
	// The <video> node, or null before mount — state (not a ref) so the observer effect re-runs
	// once it becomes available.
	videoElement: HTMLVideoElement | null;
	// Same node as a ref, for imperative reads/writes (`.muted`, `.pause()`).
	videoElementRef: React.RefObject<HTMLVideoElement | null>;
	// Attempts playback and reports whether it actually began.
	playVideo: () => Promise<boolean>;
	// Whether playback has already begun by any route. Suppresses autoplay: someone who pressed
	// play before the video was half on screen has already made their choices — including muting
	// it — and scrolling a little further shouldn't override them.
	hasStarted: boolean;
	// Called once autoplay has started, reporting whether it fell back to muted.
	onAutoplayStarted: (startedMuted: boolean) => void;
};

/**
 * Watches how much of the video is on screen via IntersectionObserver directly (needs an
 * intersection *ratio*, not just an enter/leave boundary like `Waypoint` gives), and drives two
 * scroll-triggered behaviours:
 *
 *  - Once substantially visible, attempts autoplay: unmuted first, falling back to muted since
 *    browsers block audible autoplay without user activation. Only attempted once per page view.
 *  - Once fully out of view, pauses playback so audio never plays off-screen. Does not
 *    auto-resume — the user presses play.
 *
 * No-ops on the server and when IntersectionObserver isn't available.
 */
export function useVideoScrollTriggeredAutoplay({
	videoElement,
	videoElementRef,
	playVideo,
	hasStarted,
	onAutoplayStarted,
}: Params): void {
	const [isSubstantiallyVisible, setIsSubstantiallyVisible] = useState(false);
	const [isOutOfView, setIsOutOfView] = useState(false);
	// ensures the autoplay attempt below only ever runs once per page view
	const hasAttemptedAutoplayRef = useRef(false);
	// Mirrors `isOutOfView` for the async autoplay attempt: by the time either awaited `play()`
	// resolves, a closed-over `isOutOfView` would be a stale snapshot from when the attempt began,
	// so only a ref reflects where the video is *now*.
	const isOutOfViewRef = useRef(false);

	const isIntersectionObserverSupported = useMemo(
		() => typeof window !== 'undefined' && typeof IntersectionObserver !== 'undefined',
		[],
	);

	useEffect(() => {
		if (!isIntersectionObserverSupported || !videoElement) return undefined;

		const observer = new IntersectionObserver(
			(entries) => {
				const [entry] = entries;
				// oxlint-disable-next-line typescript/no-unnecessary-condition -- destructuring an array element is typed as always-defined here (noUncheckedIndexedAccess is off), but the browser could in theory invoke the callback with an empty entries array
				if (!entry) return;
				isOutOfViewRef.current = !entry.isIntersecting;
				setIsSubstantiallyVisible(entry.intersectionRatio >= AUTOPLAY_VISIBILITY_THRESHOLD);
				setIsOutOfView(!entry.isIntersecting);
			},
			{ threshold: [0, AUTOPLAY_VISIBILITY_THRESHOLD] },
		);
		observer.observe(videoElement);

		return () => observer.disconnect();
	}, [videoElement, isIntersectionObserverSupported]);

	useEffect(() => {
		if (!isSubstantiallyVisible || hasStarted || hasAttemptedAutoplayRef.current || isMotionReduced()) return;
		hasAttemptedAutoplayRef.current = true;

		const video = videoElementRef.current;
		if (!video) return;

		void (async () => {
			/**
			 * Pauses and unmutes the video, then reports whether it should be abandoned because the
			 * user scrolled away while `play()` was still pending. The pause effect below won't catch
			 * that case (nothing is playing yet when it fires, and it won't run again), so abandoning
			 * here restores the poster/play button instead of leaving a hidden, already-"started"
			 * video decoding off-screen.
			 */
			const abandonIfGone = (): boolean => {
				if (!isOutOfViewRef.current) return false;
				video.pause();
				video.muted = false;
				return true;
			};

			video.muted = false;
			if (await playVideo()) {
				if (abandonIfGone()) return;
				onAutoplayStarted(false);
				return;
			}
			if (abandonIfGone()) return;

			video.muted = true;
			if (await playVideo()) {
				if (abandonIfGone()) return;
				onAutoplayStarted(true);
				return;
			}

			// Both attempts were rejected — revert the mute change so a later click-to-play isn't
			// silently muted.
			video.muted = false;
		})();
	}, [isSubstantiallyVisible, hasStarted, videoElementRef, playVideo, onAutoplayStarted]);

	useEffect(() => {
		if (!isOutOfView) return;
		const video = videoElementRef.current;
		if (video && !video.paused) {
			video.pause();
		}
	}, [isOutOfView, videoElementRef]);
}
